/**
 * Turning pointer gestures into a drawn shape.
 *
 * Both drawing tools end here: the pencil hands over the raw trail its pointer
 * left, the pen hands over the nodes and handles it was given, and this file
 * turns either into the `path` an element carries (see `elementPath` in
 * schema/shapeLibrary) — unit-box coordinates, so the outline scales with the
 * element box like every generated shape does.
 *
 * Scene coordinates in, element out. Nothing here knows about React or the
 * store, which is what makes it testable and keeps the canvas file about
 * gestures rather than geometry.
 */
import { ELEMENT_TYPES } from '../schema/documentSchema';
import { FREEFORM_SHAPE } from '../schema/shapeLibrary';

/** Perpendicular distance from `p` to the segment a→b. */
function pointToSegment(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (!len2) return Math.hypot(p.x - a.x, p.y - a.y);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * Ramer–Douglas–Peucker: drop the points that say nothing.
 *
 * A pencil stroke arrives as hundreds of samples a few pixels apart. Stored as
 * they are, every one of them is a node to save, to draw and to hit-test — for
 * a curve the eye cannot tell from a dozen. `tolerance` is the furthest a
 * dropped point may sit from the line that replaces it, in scene units.
 */
export function simplifyPoints(points, tolerance = 2) {
    if (points.length < 3) return [...points];
    const keep = new Array(points.length).fill(false);
    keep[0] = true;
    keep[points.length - 1] = true;

    const stack = [[0, points.length - 1]];
    while (stack.length) {
        const [first, last] = stack.pop();
        let worst = 0;
        let index = -1;
        for (let i = first + 1; i < last; i += 1) {
            const d = pointToSegment(points[i], points[first], points[last]);
            if (d > worst) { worst = d; index = i; }
        }
        if (worst > tolerance && index > 0) {
            keep[index] = true;
            stack.push([first, index], [index, last]);
        }
    }
    return points.filter((_, i) => keep[i]);
}

/**
 * Give a corner chain smooth handles (Catmull-Rom → Bézier).
 *
 * A simplified pencil trail is a polyline, and drawn at print size its corners
 * read as facets rather than as a hand-drawn line. This rounds it through the
 * same points it already passes through, so the shape is unchanged in outline
 * but continuous in slope. `tension` 0 is a polyline, 1/3 is the classic
 * Catmull-Rom conversion that touches every node exactly.
 */
export function smoothNodes(points, { closed = false, tension = 1 / 3 } = {}) {
    const n = points.length;
    if (n < 3) return points.map((p) => ({ x: p.x, y: p.y }));
    const at = (i) => {
        if (closed) return points[(i + n) % n];
        return points[Math.max(0, Math.min(n - 1, i))];
    };
    return points.map((p, i) => {
        const prev = at(i - 1);
        const next = at(i + 1);
        const tx = (next.x - prev.x) * tension;
        const ty = (next.y - prev.y) * tension;
        const first = !closed && i === 0;
        const last = !closed && i === n - 1;
        return {
            x: p.x,
            y: p.y,
            hIn: first ? null : [p.x - tx / 2, p.y - ty / 2],
            hOut: last ? null : [p.x + tx / 2, p.y + ty / 2],
        };
    });
}

/** Bounds of nodes AND their handles — a curve can bulge past its own nodes. */
export function nodesBounds(nodes) {
    const xs = [];
    const ys = [];
    nodes.forEach((n) => {
        xs.push(n.x); ys.push(n.y);
        if (Array.isArray(n.hIn)) { xs.push(n.hIn[0]); ys.push(n.hIn[1]); }
        if (Array.isArray(n.hOut)) { xs.push(n.hOut[0]); ys.push(n.hOut[1]); }
    });
    const x1 = Math.min(...xs);
    const y1 = Math.min(...ys);
    const x2 = Math.max(...xs);
    const y2 = Math.max(...ys);
    return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

/**
 * Scene-space nodes → an element ready for `addElements`.
 *
 * The box is the drawing's own bounds, padded to a minimum on either axis so a
 * perfectly straight stroke still has a box that can be grabbed and resized
 * (a zero-height element has no handles and no hit area).
 *
 * `stroke` carries the look the tool was set to draw with, so the pen and the
 * pencil can differ without this file knowing which is which.
 */
/**
 * Scene-space nodes → the box AND unit-box path that carry them, with the box
 * re-fitted to the outline. Shared by the drawing tools (a brand new element)
 * and by node editing (an existing one whose outline just moved).
 */
export function nodesToPathPatch(nodes, closed = false) {
    if (!nodes || nodes.length < 2) return null;
    const MIN = 1;
    const b = nodesBounds(nodes);
    const width = Math.max(b.width, MIN);
    const height = Math.max(b.height, MIN);
    // Flat in one axis: centre the outline in the padded box rather than
    // pinning it to an edge, so the stroke sits in the middle of its own box.
    const ox = b.x - (width - b.width) / 2;
    const oy = b.y - (height - b.height) / 2;
    const unit = (px, py) => [(px - ox) / width, (py - oy) / height];
    return {
        x: ox,
        y: oy,
        width,
        height,
        path: {
            closed,
            nodes: nodes.map((n) => {
                const [x, y] = unit(n.x, n.y);
                const out = { x, y };
                if (Array.isArray(n.hIn)) {
                    const [hx, hy] = unit(n.hIn[0], n.hIn[1]);
                    out.hIn = [hx, hy];
                }
                if (Array.isArray(n.hOut)) {
                    const [hx, hy] = unit(n.hOut[0], n.hOut[1]);
                    out.hOut = [hx, hy];
                }
                return out;
            }),
        },
    };
}

/**
 * Path COMMANDS (element-local pixels, as `shapeCommands` gives them) back into
 * nodes — how a catalogue shape becomes an editable path the moment someone
 * starts dragging its anchors. Quadratics are raised to cubics, since a node
 * pair only knows about cubic handles.
 */
export function commandsToNodes(cmds = [], w = 1, h = 1) {
    const nodes = [];
    let closed = false;
    const unitX = (v) => v / (w || 1);
    const unitY = (v) => v / (h || 1);
    cmds.forEach((c) => {
        const prev = nodes[nodes.length - 1];
        if (c.c === 'M' || c.c === 'L') {
            nodes.push({ x: unitX(c.x), y: unitY(c.y) });
        } else if (c.c === 'Q' && prev) {
            // Q → C: the cubic controls sit two thirds of the way to the single
            // quadratic control, from each end.
            const px = prev.x * (w || 1);
            const py = prev.y * (h || 1);
            prev.hOut = [unitX(px + (2 / 3) * (c.cx - px)), unitY(py + (2 / 3) * (c.cy - py))];
            nodes.push({
                x: unitX(c.x),
                y: unitY(c.y),
                hIn: [unitX(c.x + (2 / 3) * (c.cx - c.x)), unitY(c.y + (2 / 3) * (c.cy - c.y))],
            });
        } else if (c.c === 'C') {
            if (prev) prev.hOut = [unitX(c.c1x), unitY(c.c1y)];
            nodes.push({ x: unitX(c.x), y: unitY(c.y), hIn: [unitX(c.c2x), unitY(c.c2y)] });
        } else if (c.c === 'Z') {
            closed = true;
        }
    });
    // A closed outline usually walks back to its first point; that repeat is the
    // closing segment, not a node of its own.
    if (closed && nodes.length > 2) {
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6) {
            if (last.hIn) first.hIn = last.hIn;
            nodes.pop();
        }
    }
    return { closed, nodes };
}

export function nodesToElement(nodes, { closed = false, stroke = {}, name = 'Vector path' } = {}) {
    const patch = nodesToPathPatch(nodes, closed);
    if (!patch) return null;
    return {
        type: ELEMENT_TYPES.SHAPE,
        shape: FREEFORM_SHAPE,
        name,
        ...patch,
        // A drawn line is a LINE: stroked, not filled. A closed path can be given
        // a fill afterwards from the panel like any other shape. The keys are the
        // SHAPE ones (see SHAPE_BORDER_KEYS) — `stroke*`, not `border*`.
        styles: {
            // "No fill" in this schema is a SOLID fill of `transparent` — an
            // unknown fillType falls through to the gradient branch of
            // resolveFillBackground and paints a grey ramp.
            fillType: 'solid',
            fill: 'transparent',
            strokeWidth: 2,
            stroke: '#18181b',
            strokeStyle: 'solid',
            ...stroke,
        },
    };
}

/** Scene-space nodes of an element's stored path — the inverse of the above. */
export function elementNodesInScene(el) {
    const nodes = el?.path?.nodes;
    if (!Array.isArray(nodes)) return [];
    const toScene = (x, y) => ({ x: el.x + x * el.width, y: el.y + y * el.height });
    return nodes.map((n) => {
        const p = toScene(n.x, n.y);
        if (Array.isArray(n.hIn)) {
            const h = toScene(n.hIn[0], n.hIn[1]);
            p.hIn = [h.x, h.y];
        }
        if (Array.isArray(n.hOut)) {
            const h = toScene(n.hOut[0], n.hOut[1]);
            p.hOut = [h.x, h.y];
        }
        return p;
    });
}

/**
 * Unit-box nodes for a shape drawn from its BOX rather than from a path —
 * a rectangle, an ellipse, a line. Double-clicking one of those to edit its
 * anchors has to start from an outline, and this is the outline it already
 * has. (Corner radius is dropped: once the corners are anchors, the radius has
 * nowhere left to live.)
 */
export function boxShapeNodes(shape) {
    // Circle through four points: the classic Bézier constant.
    const K = 0.5522847498 / 2;
    if (shape === 'ellipse' || shape === 'circle') {
        const nodes = [
            { x: 0.5, y: 0, hIn: [0.5 - K, 0], hOut: [0.5 + K, 0] },
            { x: 1, y: 0.5, hIn: [1, 0.5 - K], hOut: [1, 0.5 + K] },
            { x: 0.5, y: 1, hIn: [0.5 + K, 1], hOut: [0.5 - K, 1] },
            { x: 0, y: 0.5, hIn: [0, 0.5 + K], hOut: [0, 0.5 - K] },
        ];
        return { closed: true, nodes };
    }
    if (shape === 'line') {
        return { closed: false, nodes: [{ x: 0, y: 0.5 }, { x: 1, y: 0.5 }] };
    }
    return {
        closed: true,
        nodes: [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }],
    };
}
